import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { removeAtIndex } from "../../utils/helpers/generalHelper";
import type { IRecipe, IRecentRecipeState, IPotentialRecipe } from "../../types/recipe";

const MAX_RECIPES = 10;

const STORAGE_KEYS = {
    recentName: "recentNameRecipes",
    recentParameter: "recentParameterRecipes",
    recentImage: "recentImageRecipes",
    potentialImage: "potentialImageRecipes",
};

type RecentKind = "recentParameter" | "recentImage" | "recentName";

const loadFromLocalStorage = (kind: RecentKind | "potentialImage"): IRecipe[] | IPotentialRecipe[] => {
    try {
        const data = localStorage.getItem(STORAGE_KEYS[kind]);
        return data ? JSON.parse(data) : [];
    } catch {
        return [];
    }
};

const initialState: IRecentRecipeState = {
    recentName: loadFromLocalStorage("recentName") as IRecipe[],
    recentParameter: loadFromLocalStorage("recentParameter") as IRecipe[],
    recentImage: loadFromLocalStorage("recentImage") as IRecipe[],
    potentialImage: loadFromLocalStorage("potentialImage") as IPotentialRecipe[],
};

const saveList = (kind: RecentKind | "potentialImage", list: IRecipe[] | IPotentialRecipe[]) => {
    localStorage.setItem(STORAGE_KEYS[kind], JSON.stringify(list));
};

const addRecipeHelper = (state: IRecentRecipeState, kind: RecentKind, recipe: IRecipe) => {
    const currentList = (state[kind] ?? []) as IRecipe[];
    const newList = [recipe, ...currentList];

    state[kind] = newList.slice(0, MAX_RECIPES);
    saveList(kind, state[kind] as IRecipe[]);
};

const clearRecipeHelper = (state: IRecentRecipeState, kind: RecentKind) => {
    state[kind] = [];
    localStorage.removeItem(STORAGE_KEYS[kind]);
};

const removeRecipeHelper = (state: IRecentRecipeState, kind: RecentKind, indexStr: string) => {
    const index = Number(indexStr);
    state[kind] = removeAtIndex(state[kind], Number(index));
    saveList(kind, state[kind] as IRecipe[]);
};

const recipesSlice = createSlice({
    name: "recipes",
    initialState,
    reducers: {
        addNameRecipe: (state, action: PayloadAction<IRecipe>) => {
            addRecipeHelper(state, "recentName", action.payload);
        },
        clearNameRecipes: (state) => {
            clearRecipeHelper(state, "recentName");
        },
        removeNameRecipe: (state, action: PayloadAction<string>) => {
            removeRecipeHelper(state, "recentName", action.payload);
        },

        addRecipe: (state, action: PayloadAction<IRecipe>) => {
            addRecipeHelper(state, "recentParameter", action.payload);
        },
        clearRecipes: (state) => {
            clearRecipeHelper(state, "recentParameter");
        },
        removeRecipe: (state, action: PayloadAction<string>) => {
            removeRecipeHelper(state, "recentParameter", action.payload);
        },

        addImageRecipe: (state, action: PayloadAction<IRecipe>) => {
            addRecipeHelper(state, "recentImage", action.payload);
        },
        clearImageRecipes: (state) => {
            clearRecipeHelper(state, "recentImage");
        },
        removeImageRecipe: (state, action: PayloadAction<string>) => {
            removeRecipeHelper(state, "recentImage", action.payload);
        },
        updatePotentialImageRecipes: (state, action: PayloadAction<IPotentialRecipe[]>) => {
            state["potentialImage"] = action.payload;
            saveList("potentialImage", state["potentialImage"] as IPotentialRecipe[]);
        },
        clearPotentialImageRecipe: (state) => {
            state["potentialImage"] = [];
            localStorage.removeItem("potentialImageRecipes");
        },
        generatePotentialImageRecipes: (state, action: PayloadAction<{ generationId: string; index: number }>) => {
            state["potentialImage"][action.payload.index] = {
                ...state["potentialImage"][action.payload.index],
                generated: true,
                generationId: action.payload.generationId,
            };
        },
        ungeneratePotentialImageRecipes: (state, action: PayloadAction<{ generationId?: string }>) => {
            const newList = state["potentialImage"].map((item) => {
                if (item.generationId !== action.payload.generationId) return item;

                return {
                    ...item,
                    generated: false,
                    generationId: undefined,
                };
            });

            state["potentialImage"] = newList;

            saveList("potentialImage", state["potentialImage"] as IPotentialRecipe[]);
        },
    },
});

export const {
    addNameRecipe,
    clearNameRecipes,
    removeNameRecipe,
    addRecipe,
    clearRecipes,
    removeRecipe,
    addImageRecipe,
    clearImageRecipes,
    removeImageRecipe,
    updatePotentialImageRecipes,
    clearPotentialImageRecipe,
    generatePotentialImageRecipes,
    ungeneratePotentialImageRecipes,
} = recipesSlice.actions;
export default recipesSlice.reducer;
